import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import * as htmlToImage from "html-to-image";
import jsPDF from "jspdf";
import styles from "./RemitoDispatch.module.css";
import { getSession } from "../../utils/sessionHandler";
import df from "../../utils/dateFormater";
import { FiCheckCircle } from "react-icons/fi";

export default function RemitoDispatch({
  dispatchData,
  onGenerated,
  isOriginal = true,
}) {
  const { copiesCount = 1 } = dispatchData;
  const wrapperRef = useRef(null);
  const barcodeRefs = useRef([]);

  const fullReference = `R${getSession().IdDistributorCompany.replaceAll(
    "-",
    ""
  )}${dispatchData.dispatchNumber}`;

  // render barcodes for each page
  useEffect(() => {
    barcodeRefs.current.forEach((ref) => {
      if (ref) {
        JsBarcode(ref, fullReference, {
          format: "CODE128",
          width: 2,
          height: 45,
          displayValue: false,
        });
      }
    });
  }, [fullReference, copiesCount]);

  // generate PDF after render
  useEffect(() => {
    const generatePDF = async () => {
      await new Promise((res) => setTimeout(res, 500));
      const wrappers = wrapperRef.current.querySelectorAll(
        `.${styles.remitoWrapper}`
      );
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      wrappers.forEach(async (node, i) => {
        const dataUrl = await htmlToImage.toJpeg(node, {
          quality: 1,
          backgroundColor: "#fff",
          useCORS: true,
        });
        if (i > 0) pdf.addPage();
        pdf.addImage(dataUrl, "JPEG", 0, 0, 210, 297);
        if (i === wrappers.length - 1) {
          pdf.save(`dispatch_${dispatchData.dispatchNumber}.pdf`);
          onGenerated && onGenerated();
        }
      });
    };
    generatePDF();
  }, [dispatchData, copiesCount, onGenerated]);

  return (
    <div ref={wrapperRef} className={styles.offscreenWrapper}>
      {Array.from({ length: copiesCount }).map((_, idx) => {
        const copyLabel = idx === 0 && isOriginal ? "ORIGINAL" : `COPY ${idx}`;
        return (
          <div key={idx} className={styles.remitoWrapper}>
            <table className={styles.headerTable}>
              <tbody>
                <tr>
                  <td rowSpan={3} className={styles.logoCell}>
                    {getSession().Company?.Logo ? (
                      <img
                        src={getSession().Company.Logo}
                        alt={getSession().Company.Name}
                      />
                    ) : (
                      <div className={styles.logoPlaceholder} />
                    )}
                  </td>
                  <td className={styles.titleCell}>DISPATCH</td>
                  <td className={styles.copyCell}>{copyLabel}</td>
                  <td rowSpan={3} className={styles.barcodeCell}>
                    <svg ref={(el) => (barcodeRefs.current[idx] = el)}></svg>
                  </td>
                </tr>
                <tr>
                  <td className={styles.labelCell}>Destination Area</td>
                  <td>{dispatchData.areaName || "–"}</td>
                </tr>
                <tr>
                  <td className={styles.labelCell}>Reference</td>
                  <td>{fullReference}</td>
                </tr>
                <tr>
                  <td colSpan={4} className={styles.subHeaderCell}>
                    <table className={styles.subHeaderTable}>
                      <tbody>
                        <tr>
                          <td className={styles.labelCell}>Picked up by</td>
                          <td>{dispatchData.shippingResponsible}</td>
                          <td className={styles.labelCell}>Date / Time</td>
                          <td>{df(dispatchData.creationDate, "dateTime")}</td>
                          <td className={styles.labelCell}>Page</td>
                          <td className={styles.pageValueCell}>1/1</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className={styles.sectionTitle}>DISPATCH DETAILS</div>
            {dispatchData.observations && (
              <p className={styles.dispatchObservations}>
                {dispatchData.observations}
              </p>
            )}

            <table className={styles.remitoTable}>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Serial</th>
                  <th>Quantity</th>
                  <th>Implant</th>
                </tr>
              </thead>
              <tbody>
                {dispatchData.packages.map((pkg) => (
                  <React.Fragment key={pkg.packageId}>
                    <tr className={styles.rowPackageHeader}>
                      <td colSpan={4}>
                        <strong>Package:</strong> {pkg.packageName} / No.{" "}
                        {pkg.packageNumber}
                      </td>
                    </tr>
                    {pkg.observations && (
                      <tr className={styles.rowPackageObs}>
                        <td colSpan={4}>
                          <em>Remarks: {pkg.observations}</em>
                        </td>
                      </tr>
                    )}
                    {pkg.materials.map((m, j) => (
                      <tr key={j}>
                        <td>{m.materialName}</td>
                        <td>{m.serial || "—"}</td>
                        <td className={styles.centerCell}>{m.quantity}</td>
                        <td className={styles.centerCell}>
                          {m.isImplant ? <FiCheckCircle /> : "—"}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className={styles.signatureContainer}>
              <div className={styles.signatureBlock}>
                <div className={styles.signatureLine} />
                <span>Sender Signature</span>
              </div>
              <div className={styles.signatureBlock}>
                <div className={styles.signatureLine} />
                <span>Carrier Signature</span>
              </div>
              <div className={styles.signatureBlock}>
                <div className={styles.signatureLine} />
                <span>Receiver Signature</span>
              </div>
            </div>

            <div className={styles.footerSpace} />
          </div>
        );
      })}
    </div>
  );
}
